import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  LogOut,
  Check,
  X,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Clock,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  adminLogin,
  adminConfirmBooking,
  adminDeclineBooking,
  adminMarkDepositPaid,
  listBookings,
  listContact,
  setAdminPasscode,
  getAdminPasscode,
  clearAdminPasscode,
} from '@/lib/api';
import { LUXE } from '@/constants/testIds';

const StatusPill = ({ value, tone }) => {
  const tones = {
    gold: 'bg-[#C9A24A]/15 text-[#C9A24A] border-[#C9A24A]/40',
    green: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
    red: 'bg-rose-500/10 text-rose-300 border-rose-500/40',
    gray: 'bg-white/5 text-white/60 border-white/15',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-[0.24em] border ${tones[tone] || tones.gray}`}
    >
      {value}
    </span>
  );
};

const paymentPill = (v) => {
  if (v === 'deposit_paid') return <StatusPill value="Deposit Paid" tone="green" />;
  if (v === 'deposit_pending') return <StatusPill value="Deposit Pending" tone="gold" />;
  return <StatusPill value="No Deposit" tone="gray" />;
};

const statusPill = (v) => {
  if (v === 'confirmed') return <StatusPill value="Confirmed" tone="green" />;
  if (v === 'declined') return <StatusPill value="Declined" tone="red" />;
  return <StatusPill value="Pending" tone="gold" />;
};

const AdminLoginView = ({ onSuccess }) => {
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setLoading(true);
    try {
      await adminLogin(passcode);
      setAdminPasscode(passcode);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-[80vh] flex items-center">
      <form
        onSubmit={handleSubmit}
        data-testid={LUXE.adminLoginForm}
        className="luxe-container max-w-md mx-auto"
      >
        <div className="luxe-card p-10">
          <div className="luxe-eyebrow mb-3">Restricted</div>
          <h1 className="text-3xl font-serif text-white mb-4">Admin Access</h1>
          <div className="gold-divider mb-6" />
          <label className="luxe-eyebrow block mb-2">Passcode</label>
          <input
            type="password"
            autoFocus
            required
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            data-testid={LUXE.adminLoginInput}
            placeholder="Enter admin passcode"
            className="luxe-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid={LUXE.adminLoginSubmit}
            className="btn-gold w-full mt-6 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Enter Dashboard'}
          </button>
        </div>
      </form>
    </div>
  );
};

const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState('all'); // all | pending | confirmed | declined

  const load = async () => {
    setLoading(true);
    try {
      const data = await listBookings();
      setBookings(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return bookings;
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const withBusy = async (key, fn) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await fn();
      await load();
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const stats = useMemo(
    () => ({
      total: bookings.length,
      pending: bookings.filter((b) => b.status === 'pending').length,
      confirmed: bookings.filter((b) => b.status === 'confirmed').length,
      depositPaid: bookings.filter((b) => b.payment_status === 'deposit_paid').length,
    }),
    [bookings]
  );

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: 'Total', value: stats.total, tone: 'text-white' },
          { label: 'Pending', value: stats.pending, tone: 'text-[#C9A24A]' },
          { label: 'Confirmed', value: stats.confirmed, tone: 'text-emerald-300' },
          { label: 'Deposits Paid', value: stats.depositPaid, tone: 'text-emerald-300' },
        ].map((s) => (
          <div key={s.label} className="luxe-card p-6">
            <div className="luxe-eyebrow">{s.label}</div>
            <div className={`text-4xl font-serif mt-2 ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'confirmed', 'declined'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.24em] border transition-colors ${
                filter === f
                  ? 'bg-[#C9A24A] text-[#0B0F1A] border-[#C9A24A]'
                  : 'text-white/60 border-[#C9A24A]/30 hover:border-[#C9A24A]/60 hover:text-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="admin-refresh"
          className="text-xs uppercase tracking-[0.24em] text-[#C9A24A] flex items-center gap-2 hover:text-white transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-white/50 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="luxe-card p-8 text-center text-white/50">
          No bookings match this filter.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((b) => (
            <div
              key={b.id}
              data-testid={LUXE.adminBookingRow}
              className="luxe-card p-6"
            >
              <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl text-white font-serif">{b.name}</h3>
                    {statusPill(b.status)}
                    {paymentPill(b.payment_status)}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[#C9A24A]">
                    {b.service} · {b.vehicle_type}
                  </div>
                </div>
                <div className="text-right">
                  <div className="package-price text-2xl">${(b.subtotal || 0).toFixed(2)}</div>
                  <div className="text-white/50 text-xs uppercase tracking-[0.24em]">
                    Deposit ${(b.deposit_amount || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-2 text-white/70 text-sm">
                <div className="flex items-center gap-2">
                  <Phone size={12} className="text-[#C9A24A]" />
                  <a href={`tel:${b.phone}`} className="hover:text-[#C9A24A]">
                    {b.phone}
                  </a>
                </div>
                {b.email && (
                  <div className="flex items-center gap-2 break-all">
                    <Mail size={12} className="text-[#C9A24A]" />
                    <a href={`mailto:${b.email}`} className="hover:text-[#C9A24A]">
                      {b.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar size={12} className="text-[#C9A24A]" /> {b.preferred_date}
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-[#C9A24A]" /> {b.preferred_time}
                </div>
                {b.location && (
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <MapPin size={12} className="text-[#C9A24A]" /> {b.location}
                  </div>
                )}
              </div>

              {b.add_ons && b.add_ons.length > 0 && (
                <div className="mt-3 text-white/60 text-xs">
                  Add-ons: <span className="text-white/80">{b.add_ons.join(', ')}</span>
                </div>
              )}

              {b.notes && (
                <div className="mt-3 text-white/60 text-xs italic border-l border-[#C9A24A]/30 pl-3">
                  {b.notes}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-white/5 flex flex-wrap items-center gap-3">
                {b.status !== 'confirmed' && (
                  <button
                    type="button"
                    data-testid={`${LUXE.adminConfirmBtn}-${b.id}`}
                    onClick={() =>
                      withBusy(`confirm-${b.id}`, () =>
                        adminConfirmBooking(b.id).then(() =>
                          toast.success('Booking confirmed & customer notified')
                        )
                      )
                    }
                    disabled={busy[`confirm-${b.id}`]}
                    className="px-4 py-2 text-[10px] uppercase tracking-[0.24em] bg-[#C9A24A] text-[#0B0F1A] font-semibold hover:bg-[#d4af37] transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Check size={12} /> Confirm
                  </button>
                )}
                {b.status !== 'declined' && (
                  <button
                    type="button"
                    data-testid={`${LUXE.adminDeclineBtn}-${b.id}`}
                    onClick={() =>
                      withBusy(`decline-${b.id}`, () =>
                        adminDeclineBooking(b.id).then(() => toast.success('Booking declined'))
                      )
                    }
                    disabled={busy[`decline-${b.id}`]}
                    className="px-4 py-2 text-[10px] uppercase tracking-[0.24em] border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <X size={12} /> Decline
                  </button>
                )}
                {b.payment_status !== 'deposit_paid' && (
                  <button
                    type="button"
                    data-testid={`${LUXE.adminMarkPaidBtn}-${b.id}`}
                    onClick={() =>
                      withBusy(`paid-${b.id}`, () =>
                        adminMarkDepositPaid(b.id).then(() => toast.success('Marked as paid'))
                      )
                    }
                    disabled={busy[`paid-${b.id}`]}
                    className="px-4 py-2 text-[10px] uppercase tracking-[0.24em] border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <DollarSign size={12} /> Mark Deposit Paid
                  </button>
                )}
                <span className="text-white/30 text-[10px] uppercase tracking-[0.24em] ml-auto">
                  ID: {b.id.slice(0, 8)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listContact();
      setMessages(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-white/50 flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="luxe-card p-8 text-center text-white/50">
        No contact messages yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((m) => (
        <div key={m.id} data-testid={LUXE.adminMessageRow} className="luxe-card p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg text-white font-serif">{m.name}</h3>
              <a
                href={`mailto:${m.email}`}
                className="text-[#C9A24A] text-sm hover:underline break-all"
              >
                {m.email}
              </a>
            </div>
            <span className="text-white/40 text-xs">
              {new Date(m.created_at).toLocaleString()}
            </span>
          </div>
          <p className="mt-3 text-white/75 leading-relaxed whitespace-pre-wrap">
            {m.message}
          </p>
        </div>
      ))}
    </div>
  );
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(!!getAdminPasscode());
  const [tab, setTab] = useState('bookings');

  if (!authed) return <AdminLoginView onSuccess={() => setAuthed(true)} />;

  return (
    <div className="pt-28 pb-16" data-testid={LUXE.adminDashboard}>
      <div className="luxe-container">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="luxe-eyebrow mb-2">Admin</div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white">
              Studio <span className="italic text-[#C9A24A]">Dashboard</span>
            </h1>
          </div>
          <button
            type="button"
            data-testid={LUXE.adminLogout}
            onClick={() => {
              clearAdminPasscode();
              setAuthed(false);
            }}
            className="text-xs uppercase tracking-[0.24em] text-white/60 hover:text-[#C9A24A] flex items-center gap-2 transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/5">
          {[
            { id: 'bookings', label: 'Bookings', testId: LUXE.adminTabBookings },
            { id: 'messages', label: 'Messages', testId: LUXE.adminTabMessages },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={t.testId}
              className={`px-5 py-3 text-xs uppercase tracking-[0.24em] transition-colors relative ${
                tab === t.id ? 'text-[#C9A24A]' : 'text-white/50 hover:text-white'
              }`}
            >
              {t.label}
              {tab === t.id && (
                <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#C9A24A]" />
              )}
            </button>
          ))}
        </div>

        {tab === 'bookings' ? <BookingsTab /> : <MessagesTab />}
      </div>
    </div>
  );
};

export default AdminPage;
